import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  User,
  Calendar,
  Download,
  Star,
  Award,
  CheckCircle2,
  MapPin,
  Globe,
  Twitter,
  Facebook,
  Instagram,
  Youtube,
  ArrowLeft,
  FileText,
  DollarSign,
  Gift,
} from 'lucide-react';
import { useSequencerProfile } from '@/hooks/useSequencerProfile';
import { SequenceCard } from '@/components/sequences/SequenceCard';

/**
 * Dedicated Creator Profile Page
 *
 * Displays:
 * - Complete user profile (avatar, name, bio, badges, social links)
 * - Aggregate statistics (sequences, downloads, ratings)
 * - Grid of all sequences created by this user
 *
 * Route: /sequencer/:username
 */
export default function SequencerProfile() {
  const { username } = useParams<{ username: string }>();

  const { profile, isLoading, error } = useSequencerProfile({ username });

  // Social media icon mapping
  const getSocialIcon = (platform: string) => {
    const lower = platform.toLowerCase();
    if (lower.includes('twitter') || lower.includes('x.com')) return Twitter;
    if (lower.includes('facebook')) return Facebook;
    if (lower.includes('instagram')) return Instagram;
    if (lower.includes('youtube')) return Youtube;
    return Globe;
  };

  // Get initials for fallback avatar
  const getInitials = (name: string) => {
    const words = name.trim().split(/\\s+/);
    if (words.length >= 2) {
      return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const formatJoinDate = (date: string) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <User className="h-16 w-16 text-muted-foreground mx-auto mb-4 animate-pulse" />
          <p className="text-muted-foreground">Loading creator profile...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !profile) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md">
          <User className="h-16 w-16 text-destructive mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Creator Not Found</h1>
          <p className="text-muted-foreground mb-6">
            {error || 'The creator profile you\'re looking for doesn\'t exist.'}
          </p>
          <Button asChild>
            <Link to="/sequencers">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Browse All Creators
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const creatorName = profile.displayName || profile.username || 'Unknown Creator';

  return (
    <div className="min-h-screen bg-background">
      {/* Back Button */}
      <div className="container max-w-6xl mx-auto pt-8 px-4">
        <Button variant="ghost" asChild className="mb-4">
          <Link to="/sequencers">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Creators
          </Link>
        </Button>
      </div>

      {/* Profile Header */}
      <section className="bg-gradient-to-br from-primary/10 via-background to-secondary/10 py-12 px-4">
        <div className="container max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row gap-8 items-start">
            {/* Avatar */}
            <div className="relative w-32 h-32 rounded-full overflow-hidden bg-gradient-to-br from-purple-500/20 to-pink-500/10 flex items-center justify-center shrink-0 border-4 border-background shadow-xl">
              {profile.avatarUrl ? (
                <img
                  src={profile.avatarUrl}
                  alt={creatorName}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : null}
              <div
                className={`absolute inset-0 flex items-center justify-center ${
                  profile.avatarUrl ? 'opacity-0' : ''
                }`}
              >
                {creatorName !== 'Unknown Creator' ? (
                  <span className="text-3xl font-bold text-purple-600">
                    {getInitials(creatorName)}
                  </span>
                ) : (
                  <User className="h-16 w-16 text-purple-600" />
                )}
              </div>
            </div>

            {/* Profile Info */}
            <div className="flex-1">
              {/* Name and Badges */}
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <h1 className="text-4xl font-bold">{creatorName}</h1>
                {profile.featured && (
                  <Badge variant="default" className="gap-1">
                    <Award className="h-4 w-4" />
                    Featured
                  </Badge>
                )}
                {profile.verified && (
                  <Badge variant="secondary" className="gap-1">
                    <CheckCircle2 className="h-4 w-4" />
                    Verified
                  </Badge>
                )}
              </div>

              {/* Username */}
              {profile.username && (
                <p className="text-muted-foreground mb-3">@{profile.username}</p>
              )}

              {/* Bio */}
              {profile.bio && (
                <p className="text-lg mb-4 max-w-3xl whitespace-pre-wrap">{profile.bio}</p>
              )}

              {/* Meta Info */}
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-4">
                {profile.createdAt && (
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span>Member since {formatJoinDate(profile.createdAt)}</span>
                  </div>
                )}
                {profile.location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    <span>{profile.location}</span>
                  </div>
                )}
                {profile.websiteUrl && (
                  <a
                    href={profile.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 hover:text-primary transition-colors"
                  >
                    <Globe className="h-4 w-4" />
                    <span>Website</span>
                  </a>
                )}
              </div>

              {/* Social Links */}
              {profile.socialLinks && Object.keys(profile.socialLinks).length > 0 && (
                <div className="flex gap-3">
                  {Object.entries(profile.socialLinks).map(([platform, url]) => {
                    const Icon = getSocialIcon(platform);
                    return (
                      <a
                        key={platform}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-full bg-background hover:bg-accent transition-colors"
                      >
                        <Icon className="h-5 w-5 text-muted-foreground hover:text-foreground" />
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Cards */}
      <section className="container max-w-6xl mx-auto -mt-8 px-4 mb-12">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          {/* Total Sequences */}
          <Card>
            <CardContent className="p-6 text-center">
              <div className="flex items-center justify-center mb-2">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div className="text-3xl font-bold text-primary">{profile.totalSequences}</div>
              <div className="text-sm text-muted-foreground mt-1">
                {profile.totalSequences === 1 ? 'Sequence' : 'Sequences'}
              </div>
            </CardContent>
          </Card>

          {/* Free Sequences */}
          <Card>
            <CardContent className="p-6 text-center">
              <div className="flex items-center justify-center mb-2">
                <Gift className="h-5 w-5 text-green-600" />
              </div>
              <div className="text-3xl font-bold text-green-600">{profile.freeSequenceCount}</div>
              <div className="text-sm text-muted-foreground mt-1">Free</div>
            </CardContent>
          </Card>

          {/* Paid Sequences */}
          <Card>
            <CardContent className="p-6 text-center">
              <div className="flex items-center justify-center mb-2">
                <DollarSign className="h-5 w-5 text-blue-600" />
              </div>
              <div className="text-3xl font-bold text-blue-600">{profile.paidSequenceCount}</div>
              <div className="text-sm text-muted-foreground mt-1">Premium</div>
            </CardContent>
          </Card>

          {/* Total Downloads */}
          <Card>
            <CardContent className="p-6 text-center">
              <div className="flex items-center justify-center mb-2">
                <Download className="h-5 w-5 text-primary" />
              </div>
              <div className="text-3xl font-bold text-primary">
                {profile.totalDownloads.toLocaleString()}
              </div>
              <div className="text-sm text-muted-foreground mt-1">Downloads</div>
            </CardContent>
          </Card>

          {/* Average Rating */}
          <Card>
            <CardContent className="p-6 text-center">
              <div className="flex items-center justify-center mb-2">
                <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
              </div>
              <div className="text-3xl font-bold text-yellow-600">
                {profile.averageRating > 0 ? profile.averageRating.toFixed(1) : 'N/A'}
              </div>
              <div className="text-sm text-muted-foreground mt-1">Rating</div>
            </CardContent>
          </Card>

          {/* Review Count */}
          <Card>
            <CardContent className="p-6 text-center">
              <div className="flex items-center justify-center mb-2">
                <Star className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className="text-3xl font-bold text-muted-foreground">{profile.reviewCount}</div>
              <div className="text-sm text-muted-foreground mt-1">
                {profile.reviewCount === 1 ? 'Review' : 'Reviews'}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Sequences Section */}
      <section className="container max-w-6xl mx-auto py-12 px-4">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2">Sequences by {creatorName}</h2>
          <p className="text-muted-foreground">
            Browse all {profile.totalSequences} sequence{profile.totalSequences !== 1 ? 's' : ''}{' '}
            created by this creator
          </p>
        </div>

        {profile.sequences.length === 0 ? (
          <div className="text-center py-12 bg-muted/20 rounded-lg">
            <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              This creator hasn't uploaded any sequences yet.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {profile.sequences.map((sequence) => (
              <SequenceCard key={sequence.id} sequence={sequence} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
